/**
 * ヤギプライム紹介キャンペーン（招待する側）の表示用純関数（Vue 非依存）。
 *
 * 無料日数の付与・累積・失効の判定はサーバー側が正本で、ここでは「返ってきた値の
 * 見せ方」だけを扱う（CLAUDE.md: Directory & Feature Layout Constraints の
 * 「引数→戻り値の Vue 非依存変換・判定は utils/<domain>/」）。
 */

/** 紹介URLのパス。サーバー側でクリックログを取ってからトップへリダイレクトする */
const REFERRAL_PATH_PREFIX = '/r/'

/**
 * 共有用の紹介URL。誰が共有したかはこのコードで判別するため、
 * トップ URL + utm ではなく `/r/<code>` に寄せる。
 */
export function buildReferralUrl(origin: string, code: string): string {
  if (!origin || !code) return ''
  return `${origin.replace(/\/$/, '')}${REFERRAL_PATH_PREFIX}${encodeURIComponent(code)}`
}

/** 紹介コードとして受け付ける形（サーバー発行の英数字）。想定外の値は共有させない */
export function isValidReferralCode(code: unknown): code is string {
  return typeof code === 'string' && /^[A-Za-z0-9]{4,32}$/.test(code)
}

/**
 * 紹介成立数から獲得日数の内訳文を作る。「30日 × 2件 ＝ 60日」。
 * 件数が 0、または日数が合わないときは空文字（推測で出さない）。
 */
export function formatGrantedDaysBreakdown(
  daysPerReferral: number,
  successCount: number,
  grantedDaysTotal: number
): string {
  if (successCount <= 0 || grantedDaysTotal <= 0) return ''
  if (daysPerReferral * successCount !== grantedDaysTotal) return ''
  return `${daysPerReferral}日 × ${successCount}件 ＝ ${grantedDaysTotal}日`
}

/**
 * 文言テンプレートの `{key}` を埋める。
 * 課金ダイアログの注記のように、1文の中に件数・日付・リンクが混ざるものを
 * JSON カタログ側に自然な日本語のまま置いておくために使う。
 */
export function fillTemplate(
  template: string,
  values: Record<string, string | number>
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match
  )
}

/**
 * 無料期間の終了日時ラベル（例: `2026年10月11日23:59`）。
 *
 * **正本はサーバーの trial_end。** これは申込前の画面で「いつまで無料か」を
 * 見せるための見込み表示で、起算日を当日として日数を足しただけの値。
 * 日数が 0 以下のときは空文字（推測で日付を出さない）。
 */
export function formatFreeUntilLabel(
  days: number,
  from: Date = new Date()
): string {
  if (!Number.isFinite(days) || days <= 0) return ''
  const end = new Date(from.getTime())
  end.setDate(end.getDate() + days)
  return `${end.getFullYear()}年${end.getMonth() + 1}月${end.getDate()}日23:59`
}
